// "Install app" without digging through the browser's menu.
//
// Chrome on Android fires `beforeinstallprompt` once, early, when the site
// becomes installable — long before any page that wants an Install button has
// mounted — so the event is caught here at startup (this file is imported from
// index.js) and held until someone taps the button. iPhones never fire it
// (Safari has no install prompt), so there the button explains the manual
// steps instead.
import { useEffect, useState } from "react";
import { alertDialog } from "./dialog";

let deferredPrompt = null;
const listeners = new Set();

function notify() {
  listeners.forEach(fn => fn());
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", e => {
    // Stop Chrome's own mini-bar; the button drives the prompt instead.
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
}

export function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  // iPadOS 13+ Safari calls itself a Mac, so a Mac that takes touch is an iPad.
  return (
    /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
    (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1)
  );
}

// "prompt" — the browser can show its install dialog right now
// "ios"    — iPhone/iPad in Safari: show the Add-to-Home-Screen steps
// null     — already installed, or nothing to offer
export function useInstallOption() {
  const [, setTick] = useState(0);

  useEffect(() => {
    const fn = () => setTick(t => t + 1);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);

  if (isStandalone()) return null;
  if (deferredPrompt) return "prompt";
  if (isIos()) return "ios";
  return null;
}

// What the Install buttons run: the browser's own dialog on Android, or the
// Add-to-Home-Screen steps spelled out on an iPhone (there is no dialog there).
export async function startInstall(option) {
  if (option === "ios") {
    await alertDialog({
      title: "Install GatherYou",
      message: "In Safari, tap Share (on newer iPhones it is inside the ⋯ menu), then \"Add to Home Screen\"."
    });
    return null;
  }
  return promptInstall();
}

// Shows the browser's install dialog. The event can only be used once, so it
// is dropped afterwards whatever the answer — Chrome fires a fresh one later
// if the person dismissed it and the site is still installable.
export async function promptInstall() {
  const event = deferredPrompt;
  if (!event) return null;
  deferredPrompt = null;
  notify();
  try {
    await event.prompt();
    const choice = await event.userChoice;
    return choice?.outcome || null;
  } catch {
    return null;
  }
}
